import { RRule, ENGLISH } from "../../lib/rrule/rrule-tz.min.js";

// 'en' is implied, don't add it to this array
const availableLanguages = ["fr"];

export class RecurrenceEditor {
	values: { rrule: string; starts_on: string; };

	constructor() {
		this.values = { rrule: "", starts_on: "" };
	}

	set_values({ rrule, starts_on }) {
		this.values = { rrule, starts_on };
	}

	async load() {
		if (this._loaded) return;

		this.RRULE_STRINGS = {};
		this.RRULE_DATES = ENGLISH;

		if (availableLanguages.includes(frappe.boot.lang)) {
			if (typeof RRULE_STRINGS === "undefined" && typeof RRULE_DATES === "undefined") {
				await frappe.require(`/assets/frappe/js/lib/rrule/locales/${frappe.boot.lang}.js`);
			}
			if (typeof RRULE_STRINGS !== "undefined") {
				this.RRULE_STRINGS = RRULE_STRINGS; // eslint-disable-line no-undef
			}
			if (typeof RRULE_DATES !== "undefined") {
				this.RRULE_DATES = RRULE_DATES; // eslint-disable-line no-undef
			}
		}

		this._loaded = true;
	}

	get start_day() {
		return moment(this.values.starts_on);
	}

	get parsed_rrule() {
		return this.rrule_object?.origOptions;
	}

	get rrule_object() {
		const rruleString = this.values.rrule;
		if (rruleString) {
			return RRule.fromString(rruleString);
		}
	}

	gettext(id) {
		return this.RRULE_STRINGS?.[id] || id;
	}

	frequency_map() {
		return {
			daily: RRule.DAILY,
			weekly: RRule.WEEKLY,
			monthly: RRule.MONTHLY,
			yearly: RRule.YEARLY,
		};
	}

	get_frequency(value) {
		const map = this.frequency_map();
		return map[value];
	}

	get_default_frequency(value) {
		const map = dict_reverse(this.frequency_map());
		return map[value] ?? "daily";
	}

	weekday_map() {
		return {
			monday: RRule.MO,
			tuesday: RRule.TU,
			wednesday: RRule.WE,
			thursday: RRule.TH,
			friday: RRule.FR,
			saturday: RRule.SA,
			sunday: RRule.SU,
		};
	}

	get_by_weekday(values) {
		const map = this.weekday_map();

		const result = Object.keys(map)
			.filter((day) => values[day] === 1)
			.map((day) => map[day]);
		return result;
	}

	get_default_by_weekday(day) {
		if (this.parsed_rrule?.byweekday?.length) {
			const map = this.weekday_map();
			const selectedDays = this.parsed_rrule.byweekday.map((v) => v.weekday);
			return selectedDays.includes(map[day].weekday) ? 1 : 0;
		}
		return 0;
	}

	get_by_day_label() {
		const date_day = this.start_day.date();
		return __("Monthly on day {}", [date_day]);
	}

	get_by_pos_label() {
		const ordinals = ["", __("first"), __("second"), __("third"), __("fourth"), __("fifth")];
		const occurence = ordinals[this.get_by_pos_count()];
		return __("Monthly on the {0} {1}", [occurence, __(this.start_day.format("dddd"))]);
	}

	get_by_pos_count() {
		return Math.ceil(this.start_day.date() / 7);
	}

	refresh_dialog() {
		if (!this.dialog) return;
		const values = this.get_dialog_values(this.dialog.get_values(true));
		this.dialog.set_value("help_text", values.repeat_text);
	}

	get_fields() {
		const fields: any[] = [
			{
				fieldname: "freq",
				label: __("Frequency"),
				fieldtype: "Select",
				options: [
					{ label: __("Daily"), value: "daily" },
					{ label: __("Weekly"), value: "weekly" },
					{ label: __("Monthly"), value: "monthly" },
					{ label: __("Yearly"), value: "yearly" },
				],
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "interval",
				label: __("Frequency interval"),
				fieldtype: "Int",
				default: 1,
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "until",
				label: __("Repeat until"),
				fieldtype: "Date",
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "count",
				label: __("Repeat count"),
				fieldtype: "Int",
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "day_col",
				fieldtype: "Column Break",
			},
			{
				fieldname: "monthly_options",
				label: __("Options"),
				fieldtype: "Select",
				depends_on: "eval:doc.freq=='monthly'",
				options: [
					{ label: this.get_by_day_label(), value: "by_day" },
					{ label: this.get_by_pos_label(), value: "by_pos" },
				],
				default: "by_day",
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "monday",
				label: __("Monday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("monday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "tuesday",
				label: __("Tuesday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("tuesday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "wednesday",
				label: __("Wednesday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("wednesday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "thursday",
				label: __("Thursday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("thursday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "friday",
				label: __("Friday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("friday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "saturday",
				label: __("Saturday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("saturday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldname: "sunday",
				label: __("Sunday"),
				fieldtype: "Check",
				depends_on: "eval:doc.freq=='weekly'||doc.freq=='daily'",
				default: this.get_default_by_weekday("sunday"),
				onchange: this.refresh_dialog.bind(this),
			},
			{
				fieldtype: "Section Break",
				fieldname: "help_section",
			},
			{
				fieldtype: "Data",
				fieldname: "help_text",
				read_only: true,
			}
		].filter(Boolean);

		return fields;
	}

	async show(callback: (res: { rrule: string; repeat_till: string | null; repeat_this_event: boolean; repeat_text: string; }) => void) {
		await this.load();

		this.dialog = new frappe.ui.Dialog({
			title: __("Recurrence"),
			size: "large",
			fields: this.get_fields(),
			primary_action_label: __("Save"),
			primary_action: (values) => {
				callback(this.get_dialog_values(values));
				this.dialog.hide();
			},
		});
		this.dialog.set_secondary_action(() => {
			callback({ repeat_text: this.get_repeat_text(""), repeat_till: "", repeat_this_event: false, rrule: "FREQ=DAILY;INTERVAL=1;COUNT=1" });
			this.dialog.hide();
		})
		this.dialog.set_secondary_action_label(__("Clear"));

		this.init_dialog();
		this.dialog.show();
	}

	get_dialog_values(values) {
		if (!values.freq) {
			return {};
		}

		let repeat_till = null;

		const rule_obj = {
			freq: this.get_frequency(values.freq),
			interval: values.interval || 1,
		};

		const weekdays_values = this.get_by_weekday(values);
		if (weekdays_values?.length) {
			Object.assign(rule_obj, { byweekday: weekdays_values });
		}

		if (values.until) {
			Object.assign(rule_obj, { until: moment.utc(values.until).toDate() });
			repeat_till = moment(values.until).format("YYYY-MM-DD");
		}

		if (values.count) {
			Object.assign(rule_obj, { count: values.count });
		}

		if (values.freq == "monthly" && values.monthly_options) {
			if (values.monthly_options == "by_day") {
				Object.assign(rule_obj, { bymonthday: [moment(this.start_day).date()] });
			} else {
				const weekday_map = this.weekday_map();
				Object.assign(rule_obj, {
					byweekday: weekday_map[this.start_day.format("dddd").toLowerCase()],
					bysetpos: [this.get_by_pos_count()],
				});
			}
		}

		const newRule = new RRule(rule_obj);

		return {
			rrule: newRule.toString(),
			repeat_till: repeat_till,
			repeat_this_event: true,
			repeat_text: this.get_repeat_text(newRule),
		};
	}

	get_repeat_text(rule?: any) {
		const rrule = rule ?? this.rrule_object;
		if (!rrule || rrule.toString() === "RRULE:FREQ=DAILY;INTERVAL=1;COUNT=1") {
			return this.RRULE_STRINGS?.once ?? "Once";
		}
		return rrule?.toText((id: string) => this.gettext(id), this.RRULE_DATES);
	}

	init_dialog() {
		const field_default_values = {
			freq: this.get_default_frequency(this.parsed_rrule?.freq),
			until: this.parsed_rrule?.until ? moment(this.parsed_rrule?.until).format("YYYY-MM-DD") : null,
			count: this.parsed_rrule?.count || null,
			interval: this.parsed_rrule?.interval || 1,
			monday: this.get_default_by_weekday("monday"),
			tuesday: this.get_default_by_weekday("tuesday"),
			wednesday: this.get_default_by_weekday("wednesday"),
			thursday: this.get_default_by_weekday("thursday"),
			friday: this.get_default_by_weekday("friday"),
			saturday: this.get_default_by_weekday("saturday"),
			sunday: this.get_default_by_weekday("sunday"),
			monthly_options: this.parsed_rrule?.bysetpos ? "by_pos" : "by_day",
		};
		this.dialog.set_values(field_default_values);
	}
};

function dict_reverse(obj) {
	const new_obj = {};
	const rev_obj = Object.keys(obj).reverse();
	rev_obj.forEach(function (i, j) {
		new_obj[obj[i]] = i;
	});
	return new_obj;
}
